import { Fetcher, StrictFetcher } from '@this/src/util';
import React from 'react';
import { styled } from '@this/constants/themes';
import Header from '@this/shared/header/header';
import Footer from '@this/shared/footer/footer';
import A from '@this/shared/atoms/a';
import { reportError } from '@this/lib/bugsnag';
import type UsersJson from '../../../../domain/user/user_json';
import User from '../../../../domain/user/user';

interface Props {}

interface State {
  user: User | null;
  cdnDomain?: string;
}

interface Response {
  cdn_domain: string;
}

const LABEL_WIDTH = '200px';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2
    style={{
      marginTop: '20px',
      marginBottom: '12px',
      borderLeft: '4px solid #0080cb',
      paddingLeft: '8px',
      fontSize: '16px'
    }}
  >
    {children}
  </h2>
);

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', fontSize: '15px', padding: '10px 0', alignItems: 'flex-start' }}>
    <div style={{ width: LABEL_WIDTH, flexShrink: 0 }}>{label}</div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const TermsCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ border: '1px solid #d0d0d0', marginBottom: '16px', overflow: 'hidden', paddingBottom: '20px' }}>
    {children}
  </div>
);

const TermsCardHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '12px 16px', color: '#0080cb', borderBottom: '1px dashed #d0d0d0', margin: '0 -1px' }}>
    {children}
  </div>
);

const TermsCardRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', padding: '10px 16px' }}>{children}</div>
);

const TermsCardLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ width: '160px', flexShrink: 0 }}>{children}</div>
);

const TermsCardValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ flex: 1 }}>{children}</div>
);

class TravelTerms extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      user: null
    };
  }

  componentDidMount() {
    StrictFetcher.get<UsersJson>('/users.json', 'users/index.json').then(
      response => {
        const user = new User(response);
        this.setState({ user });
      },
      () => {
        this.setState({
          user: null
        });
      }
    );
    this.fetchCdnDomain();
  }

  async fetchCdnDomain() {
    const res = await Fetcher.get<Response>('/travel_terms.json');
    this.setState({ cdnDomain: res.cdn_domain });
  }

  render() {
    try {
      const { user, cdnDomain } = this.state;
      return (
        <Wrap>
          <div className="travel_terms__pc">
            <div>
              <Header user={user} />
              <div className="content-body">
                <div className="info-content">
                  <h1 className="info-content__title">標識、各種約款、その他</h1>

                  <SectionTitle>標識</SectionTitle>
                  <InfoRow label="業務範囲">国内旅行・海外旅行</InfoRow>
                  <InfoRow label="登録番号">東京都知事登録旅行業第2-7247号</InfoRow>
                  <InfoRow label="登録年月日">平成28年10月13日</InfoRow>
                  <InfoRow label="有効期間">令和3年10月13日から令和8年10月12日まで</InfoRow>
                  <InfoRow label="名称">株式会社トランスファーデータ</InfoRow>
                  <InfoRow label="営業所の名称">AI Travel カスタマーサクセス</InfoRow>
                  <InfoRow label="所在地">〒108-0014 東京都港区芝5丁目7番8号ズワカマツビル8階</InfoRow>
                  <InfoRow label="総合旅行業務取扱管理者">
                    <>
                      湯谷 誠
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        （総合旅行業務取扱管理者は、お客様の旅行を取り扱う営業所での取引に関し責任者です。この旅行の契約に関し担当者からの説明にご不明な点などがありましたら、ご遠慮なく表記の旅行業務取扱管理者にご質問ください）
                      </div>
                    </>
                  </InfoRow>
                  <InfoRow label="営業時間">平日9時-18時</InfoRow>
                  <InfoRow label="受託取扱企画旅行">
                    <>
                      <A
                        href="https://travel.rakuten.co.jp/corporate/rato/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        楽天ANAトラベルオンライン株式会社
                      </A>
                      <div>
                        <A
                          href="https://www.jal.co.jp/jp/ja/tours/info/operate/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          株式会社ジャルパック
                        </A>
                      </div>
                      <div>
                        <A
                          href="https://www.jre-travel.com/guide/operate/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          株式会社びゅうトラベルサービス
                        </A>
                      </div>
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>
                        ※受託取扱からのご依頼は、営業日にお伺いいたします。ご了承ください。
                      </div>
                    </>
                  </InfoRow>

                  <SectionTitle>各種約款等</SectionTitle>

                  <TermsCard>
                    <TermsCardHeader>手配旅行</TermsCardHeader>
                    <TermsCardRow>
                      <TermsCardLabel>旅行業約款</TermsCardLabel>
                      <TermsCardValue>
                        <A href={`//${cdnDomain}/images/travel_terms.pdf`} download="travel_terms.pdf">
                          手配契約の部
                        </A>
                      </TermsCardValue>
                    </TermsCardRow>
                    <TermsCardRow>
                      <TermsCardLabel>旅行条件書</TermsCardLabel>
                      <TermsCardValue>
                        <A href={`//${cdnDomain}/images/requirements.pdf`} download="requirements.pdf">
                          手配旅行条件書
                        </A>
                      </TermsCardValue>
                    </TermsCardRow>
                  </TermsCard>

                  <TermsCard>
                    <TermsCardHeader>募集型企画旅行</TermsCardHeader>
                    <TermsCardRow>
                      <TermsCardLabel>旅行業約款</TermsCardLabel>
                      <TermsCardValue>
                        <A href="/images/travel_terms/boshu_terms_combined.pdf">募集型企画旅行契約の部</A>
                      </TermsCardValue>
                    </TermsCardRow>
                    <TermsCardRow>
                      <TermsCardLabel>旅行条件書</TermsCardLabel>
                      <TermsCardValue>
                        <A href="/images/travel_terms/boshu_requirements.pdf">募集型企画旅行条件書</A>
                      </TermsCardValue>
                    </TermsCardRow>
                  </TermsCard>

                  <TermsCard>
                    <TermsCardHeader>受注型企画旅行</TermsCardHeader>
                    <TermsCardRow>
                      <TermsCardLabel>旅行業約款</TermsCardLabel>
                      <TermsCardValue>
                        <A href="/images/travel_terms/juchu_terms_combined.pdf">受注型企画旅行契約の部</A>
                      </TermsCardValue>
                    </TermsCardRow>
                    <TermsCardRow>
                      <TermsCardLabel>旅行条件書</TermsCardLabel>
                      <TermsCardValue>
                        <A href="/images/travel_terms/juchu_requirements.pdf">受注型企画旅行条件書</A>
                      </TermsCardValue>
                    </TermsCardRow>
                  </TermsCard>

                  <InfoRow label="旅行業務取扱料金">
                    <A href="/charges">取扱い料金表</A>
                  </InfoRow>

                  <SectionTitle>その他</SectionTitle>
                  <InfoRow label="個人情報保護について">
                    <A href="https://transferdata.co.jp/privacy-policy#a">個人情報保護方針</A>
                  </InfoRow>
                  <InfoRow label="所属旅行業協会名">一般社団法人日本旅行業協会正会員</InfoRow>
                  <InfoRow label="問い合わせ先">contact@aitravel.jp</InfoRow>
                </div>
              </div>
              <Footer signedIn={!!user} />
            </div>
          </div>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const Wrap = styled.div``;

export default TravelTerms;
